import { BeneficiosEnum } from "@/core/enums/beneficios";
import { validateCPF } from "@/helpers/masks";

export const LIMITE_JUSTIFICATIVA_COLABORADORES = 3000;
export const LIMITE_JUSTIFICATIVA_TERCEIROS = 1000;
export const LIMITE_JUSTIFICATIVA_MATERIAIS = 3000;
export const LIMITE_FUNCAO_PROJETO = 100;
export const LIMITE_FORMACAO_LI_DESCRICAO = 255;

export const opcoesEscolaridadeLB = [
  "graduado",
  "técnico de nível médio",
  "pós-graduado",
  "doutor",
  "mestre",
  "apoio técnico",
  "tecnólogo",
];

// Enquadramentos que não são aceitos para justificativas de terceiros
export const opcoesEnquadramentosEmpresas = [
  "Registro e Manutenção de Marcas, Patentes e Cultivares",
  "Fornecedor de Materiais",
];

const vazio = (valor) => !valor || valor.length === 0;

/**
 * Erros de preenchimento de uma linha de colaborador.
 * Regras específicas de LB (sexo, escolaridade da lista, dedicação e função no
 * projeto) só se aplicam quando trabalho === LEI_DO_BEM.
 * @returns {string[]} lista de mensagens de erro (vazia quando a linha está válida)
 */
export function getErrosColaborador(item, trabalho) {
  const erros = [];

  if (item.justificativa && item.justificativa.length > LIMITE_JUSTIFICATIVA_COLABORADORES) {
    erros.push("Texto de justificativa ultrapassou o limite de caracteres.");
  } else if (vazio(item.justificativa)) {
    erros.push("Texto de justificativa é obrigatório.");
  }

  if (!item.cpf || (item.cpf.length !== 11 && item.cpf.length !== 14) || !validateCPF(item.cpf)) {
    erros.push("CPF do colaborador está inválido.");
  }

  if (trabalho === BeneficiosEnum.LEI_DO_BEM && vazio(item.sexo)) {
    erros.push("Sexo do colaborador está inválido.");
  }

  if (
    vazio(item.escolaridade) ||
    (trabalho === BeneficiosEnum.LEI_DO_BEM &&
      !opcoesEscolaridadeLB.includes(item.escolaridade.toLowerCase()))
  ) {
    erros.push("Escolaridade do colaborador está inválida.");
  }

  if (trabalho === BeneficiosEnum.LEI_DO_BEM && vazio(item.dedicacao)) {
    erros.push("Dedicação do colaborador está inválida.");
  }

  if (trabalho === BeneficiosEnum.LEI_DO_BEM) {
    if (item.funcao_projeto && item.funcao_projeto.length > LIMITE_FUNCAO_PROJETO) {
      erros.push("Texto de função no projeto ultrapassou o limite.");
    } else if (vazio(item.funcao_projeto)) {
      erros.push("Texto de função no projeto é obrigatório.");
    }
  }

  if (trabalho === BeneficiosEnum.LEI_DE_INFORMATICA && vazio(item.formacao)) {
    erros.push("Formação do colaborador é obrigatória.");
  }

  if (
    trabalho === BeneficiosEnum.LEI_DE_INFORMATICA &&
    item.formacao === "Outra" &&
    item.formacao_li_descricao &&
    item.formacao_li_descricao.length > LIMITE_FORMACAO_LI_DESCRICAO
  ) {
    erros.push("Descrição da formação ultrapassou o limite de 255 caracteres.");
  }

  return erros;
}

/**
 * Erros de preenchimento de uma linha de serviço de terceiro.
 * Duplicidade de CNPJ/CPF no mesmo projeto só é erro fora de LI.
 * @returns {string[]}
 */
export function getErrosTerceiro(item, trabalho) {
  const erros = [];

  if (item.justificativa && item.justificativa.length > LIMITE_JUSTIFICATIVA_TERCEIROS) {
    erros.push("Texto de justificativa ultrapassou o limite de caracteres.");
  } else if (vazio(item.justificativa)) {
    erros.push("Texto de justificativa é obrigatório.");
  }

  if (item.isCnpj && (!item.cnpj || (item.cnpj.length !== 14 && item.cnpj.length !== 18))) {
    erros.push("CNPJ do terceiro está inválido.");
  } else if (!item.isCnpj && (!item.cnpj || (item.cnpj.length !== 11 && item.cnpj.length !== 14))) {
    erros.push("CPF do terceiro está inválido.");
  } else if (item.hasDuplicidades && trabalho !== BeneficiosEnum.LEI_DE_INFORMATICA) {
    erros.push(
      item.isCnpj
        ? "CNPJ do terceiro está duplicado no projeto."
        : "CPF do terceiro está duplicado no projeto.",
    );
  }

  if (vazio(item.enquadramento) || opcoesEnquadramentosEmpresas.includes(item.enquadramento)) {
    erros.push("Enquadramento do terceiro está inválido.");
  }

  return erros;
}

/**
 * Erros de preenchimento de uma linha de material (agrupamento).
 * O campo Tipo só existe em materiais LI.
 * @returns {string[]}
 */
export function getErrosMaterial(item, trabalho) {
  const erros = [];

  if (item.justificativa && item.justificativa.length > LIMITE_JUSTIFICATIVA_MATERIAIS) {
    erros.push("Texto de justificativa ultrapassou o limite de caracteres.");
  } else if (vazio(item.justificativa)) {
    erros.push("Texto de justificativa é obrigatório.");
  }

  if (trabalho === BeneficiosEnum.LEI_DE_INFORMATICA && vazio(item.tipo)) {
    erros.push("Tipo é obrigatório.");
  }

  if (item.funcao_projeto && item.funcao_projeto.length > LIMITE_FUNCAO_PROJETO) {
    erros.push("Texto de agrupamento ultrapassou o limite.");
  } else if (vazio(item.funcao_projeto)) {
    erros.push("Texto de agrupamento é obrigatório.");
  }

  return erros;
}

/**
 * Conta as pendências de um wrapper de projeto retornado por
 * formatJustificativasResponse, independente do tipo carregado
 * (colaboradores, terceiros ou materiais).
 * @returns {number}
 */
export function contarErrosProjeto(projetoWrapper, trabalho) {
  if (projetoWrapper.colaboradores) {
    return projetoWrapper.colaboradores.reduce(
      (total, item) => total + (getErrosColaborador(item, trabalho).length ? 1 : 0),
      0,
    );
  }
  if (projetoWrapper.terceiros) {
    return projetoWrapper.terceiros.reduce(
      (total, item) => total + (getErrosTerceiro(item, trabalho).length ? 1 : 0),
      0,
    );
  }
  if (projetoWrapper.materiais) {
    return projetoWrapper.materiais.reduce(
      (total, item) => total + (getErrosMaterial(item, trabalho).length ? 1 : 0),
      0,
    );
  }
  return 0;
}
